import { useEffect, useState } from "react";

import styled from "styled-components";

import { Download, SWReactIcons } from "@similarweb/icons";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { Table } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton, Popover, PopoverContent, PopoverTrigger, Tooltip, Type } from "@similarweb/ui-components";
import { Text, Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import {
    FIRST_PAGE,
    NA_VALUE,
    adIntelIsOpen,
    isAdScreenshotsHooked,
} from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { openHookModal } from "app/services/ModalService";
import { useWebsitesFavicons } from "app/services/sitesService/hooks/useWebsitesFavicons";
import { allTrackers } from "app/services/track/track";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { StyledItemIcon } from "pro-features/components/core cells/src/CoreRecentCell/StyledComponents";
import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";

import { AD_INTELLIGENCE_HOOKS } from "../../utils/adIntelligenceHooks";
import { useAdModalTable } from "../hooks/useAdModalTable";

import { ModalSectionTitle } from "./StyledComponents";

const TableWithTrackingWrapper = styled.div`
    margin-top: 24px;
`;

const TitleRow = styled(Flex)`
    line-height: 1.2;
    align-items: center;
`;

const AdSnapshotAnchor = styled.a`
    display: inline-block;
    padding: 4px 0;
`;

const HookPopoverContent = styled(PopoverContent)`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    width: 292px;
`;

const NoPublishersGATCRow = styled(Flex)`
    align-items: center;
    gap: 8px;
`;

interface IModalTableProps {
    creativeId: string;
    isVideo: boolean;
    isOverviewContext?: boolean;
    advertiserDomain?: string;
}

const getColumns = (
    totalCount: number,
    isScreenshotHooked: boolean,
    hideScreenshotColumn = false,
): ColumnsType<Record<PropertyKey, any>> => {
    const columns: ColumnsType<Record<PropertyKey, any>> = [
        {
            dataIndex: "domain",
            title: translate("display.advertising.ad.card.top.publishers.domain.column.title", { totalCount }),
            width: 412,
            render: ({ name, favicon }: { name: string; favicon: string }) => (
                <CoreWebsiteCell
                    showToolTip={false}
                    icon={favicon}
                    domain={name}
                    textAndIconMargin="8px"
                    TooltipComponent={({ children }) => (
                        <Tooltip
                            content={translate(
                                "ad.intelligence.display.advertiser.publishers.publisher.column.link.tooltip",
                                { domain: name },
                            )}
                        >
                            {children}
                        </Tooltip>
                    )}
                />
            ),
        },
        {
            dataIndex: "screenshot",
            title: translate("ad.intelligence.advertiser.ad.card.top.publishers.screenshot.column.title"),
            render: ({ link, siteName }: { link: string; siteName: string }) => {
                if (!link) {
                    return NA_VALUE;
                }

                if (isScreenshotHooked) {
                    return (
                        <Popover
                            placement="top"
                            withArrow
                            isHover
                        >
                            <PopoverTrigger asChild>
                                <AdSnapshotAnchor as="span">
                                    <Download
                                        size="xs"
                                        color={colorsPalettes.carbon[100]}
                                    />
                                </AdSnapshotAnchor>
                            </PopoverTrigger>
                            <HookPopoverContent>
                                <Text
                                    fontVariant="body2"
                                    color={colorsPalettes.carbon[400]}
                                >
                                    {translate("ad.intelligence.ad.modal.screenshot.hook.title")}
                                </Text>
                                <IconButton
                                    iconName="spark"
                                    type="trial"
                                    iconSize="xs"
                                    iconColor={colorsPalettes.carbon[0]}
                                    width="100%"
                                    dataAutomation="ad-modal-screenshot-hook-button"
                                    onClick={() => openHookModal(AD_INTELLIGENCE_HOOKS.advertiser.touch)}
                                >
                                    {translate("ad.intelligence.ad.modal.screenshot.hook.button")}
                                </IconButton>
                            </HookPopoverContent>
                        </Popover>
                    );
                }

                return (
                    <Tooltip
                        content={translate("ad.intelligence.advertiser.ad.card.top.publishers.screenshot.tooltip", {
                            siteName,
                        })}
                        placement="top"
                    >
                        <AdSnapshotAnchor
                            href={link}
                            onClick={() =>
                                allTrackers.dispatchTrackEvent("External Link", "click", "Ads/Publisher Snapshot")
                            }
                            target="_blank"
                            rel="noreferrer"
                        >
                            <Download
                                size="xs"
                                color={colorsPalettes.blue[400]}
                            />
                        </AdSnapshotAnchor>
                    </Tooltip>
                );
            },
        },
    ];

    return hideScreenshotColumn ? columns.filter((col) => col.dataIndex !== "screenshot") : columns;
};

export const ModalTable = ({ creativeId, isVideo, isOverviewContext = false, advertiserDomain }: IModalTableProps) => {
    const pageSize = isVideo ? 5 : 6;
    const [currentPage, setCurrentPage] = useState(FIRST_PAGE);
    const { data, isLoading, isError } = useAdModalTable(creativeId, currentPage, pageSize, advertiserDomain);

    const { data: favicons } = useWebsitesFavicons(["google.com"]);

    useEffect(() => {
        setCurrentPage(FIRST_PAGE);
    }, [creativeId]);

    return (
        <div>
            <BoxTitle Container={TitleRow}>
                <ModalSectionTitle>ad.intelligence.advertiser.ad.card.spotted.on.title</ModalSectionTitle>
            </BoxTitle>
            <TableWithTrackingWrapper>
                {data?.totalCount || isError || isLoading ? (
                    <Table
                        columnConfig={getColumns(
                            data?.totalCount ?? 0,
                            isOverviewContext ? !adIntelIsOpen() : isAdScreenshotsHooked(),
                            isOverviewContext && adIntelIsOpen(),
                        )}
                        data={data?.items ?? []}
                        isLoading={isLoading}
                        loaderRowCount={7}
                        pagination={{
                            pageSize,
                            total: data?.totalCount ?? 0,
                            current: currentPage,
                            onChange: (_, __, newPage) => setCurrentPage(newPage),
                        }}
                        noData={
                            isError
                                ? {
                                      title: translate("ad.intelligence.advertiser.ad.card.modal.table.no.data.title"),
                                      subtitle: translate(
                                          "ad.intelligence.advertiser.ad.card.modal.table.no.data.subtitle",
                                      ),
                                  }
                                : undefined
                        }
                    />
                ) : (
                    <NoPublishersGATCRow>
                        <StyledItemIcon
                            iconType={Type.Website}
                            iconSrc={favicons?.["google.com"] ?? ""}
                        />
                        <Text
                            fontVariant="input2"
                            color={colorsPalettes.carbon[400]}
                        >
                            {translate("ad.intelligence.ad.card.modal.table.gatc.title")}
                        </Text>
                        <Tooltip
                            content={translate("ad.intelligence.ad.card.modal.table.gatc.tooltip")}
                            placement="top"
                        >
                            <SWReactIcons
                                iconName="info"
                                size="xs"
                            />
                        </Tooltip>
                    </NoPublishersGATCRow>
                )}
            </TableWithTrackingWrapper>
        </div>
    );
};
